import React from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import cx from "classnames";
import { useDispatch, useSelector } from "react-redux";
import stylesV2 from "./AppointmentReviewV2.module.scss";
import stylesV3 from "./AppointmentReviewV3.module.scss";
import { RootState, AppDispatch } from "app/store";
import * as paths from "features/routing/paths";
import dayjs from "dayjs";
import {
  setIsEditForm,
  setEditSection,
  setAppointmentData,
  setVersion,
  selectAppointmentData,
} from "features/appointmentDataSlice";
import { CreateAppointmentArgs } from "api/createAppointmentRequest";
import { PatientReturnStatus } from "types/patientReturnStatus";
import {
  createAppointment,
  selectAppointmentBookedStatus,
  CreateAppointmentResult,
  selectCreatedAppointment,
} from "features/scheduling/appointmentCreationSlice";
import { useTrackEvent } from "hooks/useTrackEvent";
import { Button } from "components/Button";
import { resetAppointmentBooked } from "features/scheduling/appointmentCreationSlice";
import { doctorFullName, doctorDiscountNameVEH } from "utils/doctorFullName";
import { parseStartTime } from "features/appointmentTime";
import { DoctorAvatar } from "components/DoctorAvatar";
import { Office } from "types/office";
import { Doctor } from "types/doctor";
import { Slot } from "types/slot";
import { Patient } from "types/patient";
import editIcon from "images/edit.svg";
import { maskPhone } from "components/MaskedTextInput/maskPhone";
import { LoadingToast } from "../AppointmentReview/LoadingToast";
import { Modal } from "components/Modal";
import {
  RescheduleAppointmentModal,
  rescheduleAppointmentModalAria,
} from "../AppointmentReview/RescheduleAppointmentModal";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";

interface AppointmentReviewPathParams {
  officeId: string;
}

interface AppointmentReviewPopUpV2Props {
  selectedOffice: Office;
  selectedDoctor: Doctor;
  selectedSlot: Slot;
  selectedPatient: Patient;
  seeMoreScrollToTop: (visible: boolean) => void;
  closeReview: () => void;
  onPatientEdit: () => void;
  seteditSectionModal: (data: string) => void;
  onRescheduleConflict: () => void;
}

declare let REACT_APP_ENV: string | undefined;
export const AppointmentReviewPopUpV2: React.FC<AppointmentReviewPopUpV2Props> = ({
   selectedOffice,
   selectedDoctor,
   selectedSlot,
   selectedPatient,
   closeReview,
   seeMoreScrollToTop,
   seteditSectionModal,
  onPatientEdit,
  onRescheduleConflict,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const history = useHistory();
  const dispatch: AppDispatch = useDispatch();
  const { trackEvent } = useTrackEvent();
  const routeParams = useParams<AppointmentReviewPathParams>();
  let { officeId } = routeParams;
  const [submitLocked, setSubmitLocked] = React.useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = React.useState(false);
  const [isRescheduleAttempt, setIsRescheduleAttempt] = React.useState(false);
  const appointmentData = useSelector((state: RootState) =>
    selectAppointmentData(state)
  );
  
  const tempappointmentData = appointmentData;
  if (
      tempappointmentData &&
      !tempappointmentData.doctor &&
      !tempappointmentData.timeSlot
    ) {
      return false;
  }

  if (
      !selectedOffice ||
      !selectedDoctor ||
      !selectedSlot ||
      !selectedPatient) {
        return false;
      }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  // const history = useHistory();
  officeId = selectedOffice.id;
  const officeUid = selectedOffice.Uid;
  const patient = selectedPatient;
  const examType = tempappointmentData.eyeExam;
  const providerId = tempappointmentData.providerId;
  const formattedPhone = tempappointmentData.formattedPhone;
  const patientReturnStatus = tempappointmentData.patientType;
  const appointmentBookedStatus = useSelector(selectAppointmentBookedStatus);
  const createdAppointment = useSelector(selectCreatedAppointment);
  const lastRequestUrl = tempappointmentData.lastRequestUrl;
  const formattedTime = parseStartTime(selectedSlot).format(
    "h:mm A"
  );
  const { doctorId, date, startTime, endTime } = selectedSlot;
  const scheduleAppointment = (overrides?: Partial<CreateAppointmentArgs>) => {
    setSubmitLocked(true);
    setIsRescheduleAttempt(Boolean(overrides?.allowReschedule));

    dispatch(
      createAppointment({
        officeId,
        officeUid,
        providerId,
        patient,
        doctorId,
        date,
        startTime,
        endTime,
        examType,
        patientReturnStatus: patientReturnStatus as PatientReturnStatus,
        ...overrides,
      })
    );
  };

  React.useEffect(() => {
    trackEvent("step-7-account-created", {
      location: officeId,
      logicalId: selectedOffice.logicalId,
      selected_type: examType,
      patient_type: patientReturnStatus,
      doctorId: doctorId,
    });
  }, [
    createdAppointment,
    lastRequestUrl,
    history,
    officeId,
    trackEvent,
    examType,
    patientReturnStatus,
    doctorId,
  ]);

  const onClickSlot = () => {
      if (submitLocked) {
        return false;
      }
      seeMoreScrollToTop(true);
      seteditSectionModal("timeSlot");
      closeReview();
    };

  const onClickPatient = () => {
    if (submitLocked) {
      return false;
    }
    seteditSectionModal("patientDetails");
    closeReview();
  };

  React.useEffect(() => {
    dispatch(setVersion(2));
  }, [dispatch]);

  React.useEffect(() => {
    if (appointmentBookedStatus === CreateAppointmentResult.Unknown) {
      return;
    }

    switch (appointmentBookedStatus) {
      case CreateAppointmentResult.Created:
        history.push(paths.appointmentSuccessNewV2({ officeId }));
        trackEvent("step-7-account-created", {
          location: officeId,
          logicalId: selectedOffice.logicalId,
          selected_type: examType,
          patient_type: patientReturnStatus,
          doctorId: doctorId,
        });
        break;
      case CreateAppointmentResult.SlotTaken:
        history.push(
          paths.appointmentsSlotTaken({
            officeId: officeId,
            examType: examType,
            patientReturnStatus: patientReturnStatus,
          })
        );
        break;
      case CreateAppointmentResult.Failed:
        history.push(paths.appointmentsFailureV2({ officeId }));
        break;
      case CreateAppointmentResult.DuplicateAppointment:
        setSubmitLocked(false);
        if (isRescheduleAttempt) {
          setIsRescheduleAttempt(false);
          dispatch(resetAppointmentBooked());
          onRescheduleConflict();
        } else {
          setShowRescheduleModal(true);
          dispatch(resetAppointmentBooked());
        }
        break;
    }
    dispatch(setIsEditForm(false));
  }, [
    appointmentBookedStatus,
    dispatch,
    history,
    officeId,
    selectedOffice.logicalId,
    trackEvent,
    examType,
    patientReturnStatus,
    doctorId,
    isRescheduleAttempt,
    onRescheduleConflict,
  ]);
  const discountVEHDoctor = doctorDiscountNameVEH();
  const doctorName =
    selectedDoctor.offersVEH &&
    selectedOffice.discountVEH &&
    discountVEHDoctor
      ? doctorDiscountNameVEH()
      : doctorFullName(selectedDoctor);
  return (
    <>
      <LoadingToast isLoading={submitLocked} />
      <Modal
        aria={rescheduleAppointmentModalAria}
        isOpen={showRescheduleModal}
        onRequestClose={() => {
          setShowRescheduleModal(false);
          dispatch(resetAppointmentBooked());
        }}
        closeOnOverlayClick={false}
        className={styles.modal}
      >
        <RescheduleAppointmentModal
          setShowRescheduleModal={setShowRescheduleModal}
          submitLocked={submitLocked}
          scheduleAppointment={scheduleAppointment}
        />
      </Modal>
      <div>
        <div className={styles.reviewLabel}>Almost there! Does this look correct?</div>
        <div className={styles.reviewDescription}>Your appointment will not be booked until you click the "COMPLETE BOOKING" button below.</div>
        <div className={styles.reviewSlotContainer}>
            <div className={styles.rcontainerLeft}>
              <div className={styles.reditSection}>
                <div className={styles.rslotLabel}>LOCATION & DOCTOR</div>
              </div>
            <div className={styles.rslotDivider}></div>
            <div className={styles.rofficeName}>{selectedOffice.displayName}</div>
            <div className={styles.rofficeAddress}>
                {selectedOffice.address1},&nbsp;
                {selectedOffice.city},{" "}
                {selectedOffice.stateAbbr}&nbsp;
                {selectedOffice.formattedZipCode} <br /> 
                {selectedOffice.formattedPhone}
            </div>
            <div className={styles.rdoctorContainer}>
              <div className={styles.rdocImg}>
                <DoctorAvatar
                  doctor={selectedDoctor}
                  size="small"
                  selectedOffice={selectedOffice}
                />
              </div>
              <div className={styles.rdoctorName}>{doctorName}</div>
              </div>
            </div>
            <div className={styles.rcontainerRight}>
            <div className={styles.reditSection}>
              <div className={cx(styles.rslotLabel,styles.rslotDes)}>APPOINTMENT DATE & TIME</div>
              <div className={styles.redit} onClick={() => { onClickSlot(); }}>
                <img
                    className={styles.reditIcon}
                    src={editIcon}
                    alt="edit"
                /> 
                <span className={styles.reditText}>change</span>
              </div>
            </div>
            <div className={styles.rslotDivider}></div>
            <div>
                <label className={styles.rslotDateLabel}>Date: </label> 
                <span className={styles.rslotDateValue}> 
                  {dayjs(selectedSlot.date).format("ddd, MMM D, YYYY")}
                </span>
            </div>
            <div>
                <label className={styles.rslotDateLabel}>Time: </label>
                <span className={styles.rslotDateValue}>{formattedTime}</span>
            </div>
            </div>
        </div>
        <div className={styles.rptcontainer}>
            <div className={styles.reditSection}>
              <div className={cx(styles.rpatientLabel,styles.slotDes)}>PATIENT DETAILS</div>
              <div className={styles.redit} onClick={() => { onClickPatient(); }}>
                <img
                    className={styles.reditIcon}
                    src={editIcon}
                    alt="edit"
                /> 
                <span className={styles.reditText}>change</span>
              </div>
            </div>
            <div className={styles.rslotDivider}></div>
            <div className={styles.rpatientContainer}>
                <div className={styles.rPatientName}>
                <label className={styles.rPtLabel}>Patient Name: </label> 
                <span className={styles.rPtValue}>{selectedPatient.firstName} {selectedPatient.lastName}</span>
                </div>
                <div className={styles.rPDOB}>
                <label className={styles.rPtLabel}>Date of Birth: </label>
                <span className={styles.rPtValue}>
                  {dayjs(selectedPatient.dateOfBirth).format("MM/DD/YYYY")}
                </span>
                </div>
                <div className={styles.rPPhone}>
                <label className={styles.rPtLabel}>Phone: </label>
                <span className={styles.rPtValue}>{maskPhone(selectedPatient.phoneNumber)}</span>
                </div>
            </div>
            <div className={styles.rEmail}>
            <label className={styles.rPtLabel}>Preferred Email Address: </label>
                <span className={styles.rPtValue}>{selectedPatient.email}</span>
            </div>
        </div>
        <div className={styles.rmodalFooter}>
          <Button
              text="Complete Booking"
              size="large"
              onClick={() => {
                scheduleAppointment();
              }}
              disabled={submitLocked}
              loading={submitLocked}
              className={styles.rbutton}
          />
        </div>
    </div>
    </>
  );
};